import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { Unstable_Grid2 as Grid } from '@mui/material';
import PlanFields from 'components/plan/PlanFields';
import PlanPreview from 'components/plan/PlanPreview';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryLoadingButton from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultPlanFormValidations from 'common/validations/form/defaultPlan';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { createPlanInitialValues } from 'common/form/initial-values/create';
import getDataToSave from 'common/form/dataToSave';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import planSlice from 'store/modules/plan';
import plan from 'api/graphql/plan';

const requiredFields = ['name', 'price', 'sessions', 'payment_link'];

function PlanCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('plan.create', { ns: 'routes' });
  const formRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingCategories, setPendingCategories] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const status = useSelector((state) => state.plan.status);
  const currentPlan = useSelector((state) => state.plan.currentPlan);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  // Efecto para manejar la sincronización de categorías después de crear el plan
  useEffect(() => {
    const syncCategories = async () => {
      if (
        status === requestStatus.success &&
        currentPlan?.id &&
        pendingCategories &&
        pendingCategories.length > 0
      ) {
        try {
          await plan.syncSessionTypes(currentPlan.id, pendingCategories);
          await dispatch(planSlice.actions.find(currentPlan.id));
          setPendingCategories(null);
        } catch (error) {
          console.error('Error al sincronizar categorías:', error);
        }
      }
    };

    syncCategories();
  }, [status, currentPlan, pendingCategories, dispatch]);

  useEffect(() => {
    const uploadImage = async () => {
      if (status === requestStatus.success && currentPlan?.id && pendingImage) {
        try {
          await plan.uploadImage(currentPlan.id, pendingImage);
        } catch (error) {
          console.error('Error al subir imagen del plan:', error);
        } finally {
          setPendingImage(null);
        }
      }
    };

    uploadImage();
  }, [status, currentPlan, pendingImage]);

  useEffect(() => {
    if (status === requestStatus.success && !isCreating && !pendingCategories) {
      formRef.current.restart();
    }
  }, [status, isCreating, pendingCategories]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    async (values) => {
      setIsCreating(true);
      try {
        // Extraer las categorías, imagen y el resto de datos
        const { sessionTypeIds, image, ...planData } = values;
        const dataToSave = getDataToSave(planData, 'plan');

        if (sessionTypeIds && sessionTypeIds.length > 0) {
          setPendingCategories(sessionTypeIds);
        }
        if (image) {
          setPendingImage(image);
        }

        dispatch(planSlice.actions.create(dataToSave));
      } catch (error) {
        console.error('❌ Error al crear plan:', error);
      } finally {
        setIsCreating(false);
      }
    },
    [dispatch]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createPlanInitialValues}
        onSubmit={onCreate}
        validate={defaultPlanFormValidations}
        render={({ handleSubmit, form, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                <Grid xs={12} lg={7}>
                  <PlanFields requiredFields={requiredFields} form={form} />
                </Grid>
                <Grid xs={12} lg={5}>
                  <FormSpy subscription={{ values: true }}>
                    {({ values }) => <PlanPreview values={values} />}
                  </FormSpy>
                </Grid>
              </Grid>
              <StyledButtonsContainer>
                <SecondaryLoadingButton
                  component={RouterLink}
                  disabled={status === requestStatus.loading || isCreating}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listPlan.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading || isCreating}
                  loading={status === requestStatus.loading || isCreating}
                  label={t('plan.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default PlanCreate;
